import { useMemo } from 'react';

import type { FindApplicationsRequestParams } from '~domains/transactions/entities/application';
import { useFindApplicationsQuery } from '~domains/transactions/entities/application';

import { useMerchantsListQuery } from '~entities/merchant';
import type { Pos } from '~entities/pos';
import { useGetPointsOfSalesQuery } from '~entities/pos';

import type { ApplicationsTableData } from './applications-table-data';

type SuccessfulReturn = {
  isSuccess: true;
  data: { applications: ApplicationsTableData[]; total: number };
};

type UnsuccessfulReturn = {
  isSuccess: false;
  data: undefined;
};

type DefaultReturn = {
  isLoading: boolean;
  isError: boolean;
};

type UseApplicationTableReturn = DefaultReturn &
  (SuccessfulReturn | UnsuccessfulReturn);

// query for getting enriched data to display in applications table
export function useApplicationsTableDataQuery(
  requestOptions?: FindApplicationsRequestParams
): UseApplicationTableReturn {
  const queries = [
    useFindApplicationsQuery(requestOptions!, { enabled: !!requestOptions }),
    useMerchantsListQuery(),
    useGetPointsOfSalesQuery(),
  ] as const;
  const [applicationsQuery, merchantsQuery, posQuery] = queries;

  const posMap = useMemo(
    () =>
      posQuery.data?.reduce(
        (result, pos) => {
          result[pos.id] = pos;

          return result;
        },
        {} as Record<string, Pos>
      ) ?? {},
    [posQuery.data]
  );

  const isQueryLoading = queries.some((q) => q.isLoading);
  const isError = queries.some((q) => q.isError);
  const isFetching = queries.some((q) => q.isFetching);

  const isLoading =
    isQueryLoading || (isFetching && applicationsQuery.isPlaceholderData);

  if (
    posQuery.isSuccess &&
    applicationsQuery.isSuccess &&
    merchantsQuery.isSuccess
  ) {
    const { applications, total } = applicationsQuery.data;
    const shouldAddMerchants = merchantsQuery.data.length > 1;
    const enrichedApplications: ApplicationsTableData[] = applications.map(
      (application) => {
        const pos = posMap[application.pointOfSalesId];

        return {
          ...application,
          pos: {
            ...pos,
            fullAddress: pos.fullAddress.replace(/, PHL$/, ''),
            // we should display merchant name only if the manager has more than 1 merchant
            merchantName: shouldAddMerchants ? pos.merchantName : undefined,
          },
        };
      }
    );

    return {
      isSuccess: true,
      data: { applications: enrichedApplications, total },
      isLoading,
      isError,
    };
  }

  return {
    isSuccess: false,
    data: undefined,
    isLoading,
    isError,
  };
}
